import { RecommendedActivity } from './types';
import { ClockIcon, PeopleIcon, ChecklistIcon } from './icons';
import LikeCountBadge from './LikeCountBadge';
import { 
  ACTIVITY_DETAILS_COLORS, 
  ACTIVITY_DETAILS_RADIUS, 
  ACTIVITY_DETAILS_SPACING,
  ACTIVITY_DETAILS_SHADOWS,
  ACTIVITY_DETAILS_TYPOGRAPHY 
} from './constants';

interface RecommendationCardProps {
  activity: RecommendedActivity;
  onViewActivity?: (id: string) => void;
}

/**
 * RecommendationCard Component
 * 
 * Card for recommended activities in the horizontal carousel.
 * 
 * Design specs:
 * - Width: 312px
 * - Background: white
 * - Border radius: 24px
 * - Padding: 16px
 * - Shadow: 0px 4px 16px rgba(0, 124, 173, 0.2)
 * - Layout: flex column, gap 10px
 * 
 * Image: height 180px, border radius 12px
 * Title: DM Sans SemiBold 20px, #1B2831
 * Description: DM Sans Regular 14px, #323B46, 2 lines max
 * Benefit box: light blue bg, DM Sans Regular 12px, #007CAD
 * Button: full width, 48px height, dark bg
 */
export default function RecommendationCard({ activity, onViewActivity }: RecommendationCardProps) {
  // Function to render benefit text with bold keywords
  const renderBenefitText = (text: string, keywords: string[]) => {
    if (keywords.length === 0) {
      return text;
    }

    let result: (string | JSX.Element)[] = [text];

    keywords.forEach((keyword, keyIndex) => {
      const newResult: (string | JSX.Element)[] = [];
      
      result.forEach((part, partIndex) => {
        if (typeof part === 'string') {
          const parts = part.split(keyword);
          parts.forEach((p, i) => {
            if (i > 0) {
              newResult.push(
                <span key={`${keyIndex}-${partIndex}-${i}`} className="font-bold">
                  {keyword}
                </span>
              );
            }
            if (p) {
              newResult.push(p);
            }
          });
        } else {
          newResult.push(part);
        }
      });
      
      result = newResult;
    });

    return result;
  };

  return (
    <div
      className="flex flex-col bg-white overflow-hidden"
      style={{
        width: `${ACTIVITY_DETAILS_SPACING.recommendationCardWidth}px`,
        borderRadius: `${ACTIVITY_DETAILS_RADIUS.card}px`,
        padding: '16px',
        boxShadow: ACTIVITY_DETAILS_SHADOWS.card,
        gap: '10px',
      }}
    >
      {/* Image with Like Count */}
      <div
        className="relative overflow-hidden"
        style={{
          height: `${ACTIVITY_DETAILS_SPACING.cardImageHeight}px`,
          borderRadius: `${ACTIVITY_DETAILS_RADIUS.image}px`,
        }}
      >
        <img
          src={activity.imageUrl}
          alt={activity.title}
          className="w-full h-full object-cover"
        />
        {
          (activity?.likesCount ?? 0) > 0 && (
            <LikeCountBadge
              count={activity.likesCount}
              className="absolute bottom-2 right-2"
            />
          )
        }
      </div>

      {/* Title */}
      <h4
        className={`font-semibold ${ACTIVITY_DETAILS_TYPOGRAPHY.recommendationCardTitle}`}
        style={{
          fontFamily: 'DM Sans, sans-serif',
          color: ACTIVITY_DETAILS_COLORS.textPrimary,
          margin: 0,
          lineHeight: 1.2,
        }}
      >
        {activity.title}
      </h4>

      {/* Metadata Grid */}
      <div className="flex gap-4">
        {/* Left column - stacked */}
        <div className="flex flex-col gap-2 flex-1">
          <div className="flex items-center gap-1">
            <ClockIcon size={14} />
            <span
              className={`font-medium ${ACTIVITY_DETAILS_TYPOGRAPHY.recommendationCardMetadata}`}
              style={{
                fontFamily: 'DM Sans, sans-serif',
                color: ACTIVITY_DETAILS_COLORS.textPrimary,
              }}
            >
              {activity.duration}
            </span>
          </div>
          <div className="flex items-center gap-1">
            <PeopleIcon size={14} />
            <span
              className={`font-medium ${ACTIVITY_DETAILS_TYPOGRAPHY.recommendationCardMetadata}`}
              style={{
                fontFamily: 'DM Sans, sans-serif',
                color: ACTIVITY_DETAILS_COLORS.textPrimary,
              }}
            >
              {activity.playType}
            </span>
          </div>
        </div>

        {/* Right column */}
        <div className="flex gap-1 flex-1">
          <ChecklistIcon size={14} />
          <span
            className={`font-medium ${ACTIVITY_DETAILS_TYPOGRAPHY.recommendationCardMetadata}`}
            style={{
              fontFamily: 'DM Sans, sans-serif',
              color: ACTIVITY_DETAILS_COLORS.textPrimary,
            }}
          >
            {activity.prepNeeded}
          </span>
        </div>
      </div>

      {/* Description */}
      <p
        className={`font-normal ${ACTIVITY_DETAILS_TYPOGRAPHY.recommendationCardDescription}`}
        style={{
          fontFamily: 'DM Sans, sans-serif',
          lineHeight: 1.2,
          color: ACTIVITY_DETAILS_COLORS.textBody,
          margin: 0,
          minHeight: '32px',
          overflow: 'hidden',
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          textOverflow: 'ellipsis',
        }}
      >
        {activity.description}
      </p>

      {/* Benefit Box */}
      <div
        className="w-full"
        style={{
          backgroundColor: ACTIVITY_DETAILS_COLORS.infoBoxBg,
          borderRadius: `${ACTIVITY_DETAILS_RADIUS.button}px`,
          padding: '16px 12px',
        }}
      >
        <p
          className={`font-normal ${ACTIVITY_DETAILS_TYPOGRAPHY.recommendationCardMetadata}`}
          style={{
            fontFamily: 'DM Sans, sans-serif',
            lineHeight: 1.2,
            color: ACTIVITY_DETAILS_COLORS.infoBoxText,
            margin: 0,
            minHeight: '30px',
            overflow: 'hidden',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            textOverflow: 'ellipsis',
          }}
        >
          {renderBenefitText(activity.benefitText, activity.benefitKeywords)}
        </p>
      </div>

      {/* View Full Activity Button */}
      <button
        onClick={() => onViewActivity?.(activity.id)}
        className="w-full flex items-center justify-center"
        style={{
          height: `${ACTIVITY_DETAILS_SPACING.buttonHeight}px`,
          backgroundColor: ACTIVITY_DETAILS_COLORS.gray6,
          border: 'none',
          borderRadius: `${ACTIVITY_DETAILS_RADIUS.button}px`,
          cursor: 'pointer',
        }}
      >
        <span
          className="font-black uppercase"
          style={{
            fontFamily: 'DM Sans, sans-serif',
            fontSize: '12px',
            letterSpacing: '0.72px',
            color: ACTIVITY_DETAILS_COLORS.white,
          }}
        >
          Try This Spark
        </span>
      </button>
    </div>
  );
}
